import logo from "./assets/logo/logo.png";

import { 
  API_BASE_URL as THEME_API_BASE_URL, 
  FILE_BASE_URL as THEME_FILE_BASE_URL 
} from "./constants/api";

import { 
  PRIMARY_COLOR_HEX as THEME_PRIMARY_COLOR_HEX, 
  PRIMARY_HOVER_COLOR_HEX as THEME_PRIMARY_HOVER_COLOR_HEX,
  PRIMARY_BG_CLASS as THEME_PRIMARY_BG_CLASS,
  PRIMARY_HOVER_BG_CLASS as THEME_PRIMARY_HOVER_BG_CLASS,
  PRIMARY_TEXT_CLASS as THEME_PRIMARY_TEXT_CLASS,
  PRIMARY_HOVER_TEXT_CLASS as THEME_PRIMARY_HOVER_TEXT_CLASS,
  PRIMARY_BORDER_CLASS as THEME_PRIMARY_BORDER_CLASS,
  PRIMARY_RING_CLASS as THEME_PRIMARY_RING_CLASS,
  STATUS_STYLES
} from "./constants/theme";

import { 
  placeholderPoster as ASSET_placeholderPoster, 
  videoUrl as ASSET_videoUrl,
  LOGO_URL as ASSET_LOGO_URL,
  BANNER_URL as ASSET_BANNER_URL
} from "./constants/assets";

import { 
  BENEFITS as MOCK_BENEFITS, 
  JOB_LISTINGS as MOCK_JOB_LISTINGS, 
  FAQS as MOCK_FAQS 
} from "./data/mockData";

import { Benefit } from "./types/benefit";
import { Job, JobListing } from "./types/job";
import { 
  TrendingUp, 
  DollarSign, 
  BookOpen, 
  Handshake, 
  Clock, 
  CheckCircle,
  Briefcase,
  Cpu,
  Layout,
  Globe
} from "lucide-react";

// Types
export { type Benefit, type Job, type JobListing };

// Re-export STATUS_STYLES from theme
export { STATUS_STYLES };

// --- API Config ---

const host = window.location.hostname;
const origin = window.location.origin;

export const API_BASE_URL =
    host === "localhost" || host === "127.0.0.1"
        ? "https://dev.phonelab.co.in/api"
        : `${origin}/api`;

export const FILE_BASE_URL =
    host === "localhost" || host === "127.0.0.1"
        ? "https://dev.phonelab.co.in"
        : origin;

// --- 🌿 Brand Configuration (GREEN) ---

export const PRIMARY_COLOR_HEX = "#2563eb";
export const PRIMARY_HOVER_COLOR_HEX = "#2E9449";

export const PRIMARY_BG_CLASS = "bg-[#2563eb]";
export const PRIMARY_HOVER_BG_CLASS = "hover:bg-[#2E9449]";
export const PRIMARY_TEXT_CLASS = "text-[#2563eb]";
export const PRIMARY_HOVER_TEXT_CLASS = "hover:text-[#2E9449]";
export const PRIMARY_BORDER_CLASS = "border-[#2563eb]";
export const PRIMARY_RING_CLASS = "ring-[#2563eb]";

// --- Assets ---

// Logo asset from src/assets/logo/logo.png
export const LOGO_URL = logo;
// export const BANNER_URL = "/62ae4437-c637-47bc-a77f-831e7437980c.jpg";
export const BANNER_URL = "/banner-1.png";
export const videoUrl = "/68dbdb41db39d_SonaliBendre_Horizontal.mp4";
export const placeholderPoster =
    "https://placehold.co/1280x720/1f2937/ffffff?text=Loading+Brand+Video";

// --- Benefits ---

export const BENEFITS: Benefit[] = [
    {
        icon: TrendingUp,
        title: "Career Growth & Stability",
        description:
            "Structured career progression and long-term stability for dedicated employees.",
    },
    {
        icon: DollarSign,
        title: "Competitive Salary & Incentives",
        description:
            "Earn a competitive salary with performance-based bonuses and commissions.",
    },
    {
        icon: BookOpen,
        title: "Hands-On Training",
        description:
            "Stay updated with the latest technologies through our in-depth training programs.",
    },
    {
        icon: Handshake,
        title: "Supportive Environment",
        description:
            "Be part of a dynamic team that values professionalism, teamwork, and innovation.",
    },
    {
        icon: Clock,
        title: "Work-Life Balance",
        description: "Enjoy scheduled breaks and a weekly day off.",
    },
    {
        icon: CheckCircle,
        title: "Real-World Experience",
        description:
            "Gain valuable experience and career advancement opportunities across all roles.",
    },
];

// --- Job Listings ---

export const JOB_LISTINGS: JobListing[] = [
    {
        id: 1,
        title: "Basic Mobile Repair Technician",
        icon: Briefcase,
        location: "West Bengal, Full Time",
        category: "Mobile Repair",
        description:
            "Responsible for diagnosing and repairing common smartphone issues. No prior experience required—training provided!",
        details: [
            "Diagnose and repair mobile devices, including battery and display replacements.",
            "Fix charging port and microphone issues.",
            "Perform basic water damage restoration.",
            "Ensure high-quality repairs and customer satisfaction.",
            "Maintain tools and workspace efficiently.",
        ],
        experience: "Training Provided (1 Year Minimum Experience for full role)",
        salary: "₹10,920 – ₹15,600+",
        compensation:
            "Basic Salary, plus ₹10/jobsheet and a 1% commission on the total monthly invoice (excluding service charges).",
        qualification: "Madhyamik Pass (10th Standard)",
        locations: ["Medinipur City", "Belda City", "Temathani City"],
    },
    {
        id: 2,
        title: "Advanced Repair Technician (L3 / Chip Level)",
        icon: Cpu,
        category: "Mobile Repair",
        location: "West Bengal, Full Time",
        description:
            "Handle complex mobile repairs including motherboard issues, IC replacements, and micro-soldering.",
        details: [
            "Perform micro-soldering and complex IC replacements.",
            "Troubleshoot software and network issues.",
            "Diagnose PCB short-circuits and failures.",
            "Maintain repair documentation and inventory.",
        ],
        experience: "2+ years in Chip-Level Mobile Repair",
        salary: "₹18,000 – ₹28,000+",
        compensation:
            "Higher salary with bonuses based on complexity and success rate.",
        qualification:
            "Higher Secondary (12th) or ITI/Diploma in Electronics",
        locations: ["Kolkata HQ", "Medinipur City"],
    },
    {
        id: 3,
        title: "Store Manager",
        icon: Layout,
        category: "Retail",
        location: "West Bengal, Full Time",
        description:
            "Oversee daily operations, inventory, sales, and customer service.",
        details: [
            "Manage technicians and front-desk staff.",
            "Ensure customer satisfaction.",
            "Track sales targets and inventory.",
            "Handle local marketing initiatives.",
        ],
        experience: "2+ years in Retail/Service Management",
        salary: "₹25,000 – ₹40,000+",
        compensation:
            "Fixed salary with performance-based store bonus.",
        qualification: "Undergraduate Degree (any discipline)",
        locations: ["All Major City Locations"],
    },
    {
        id: 4,
        title: "Brand Marketing Specialist",
        icon: Globe,
        category: "Marketing",
        location: "West Bengal, Full Time (Hybrid)",
        description:
            "Drive brand growth through campaigns and digital marketing.",
        details: [
            "Plan and execute marketing campaigns.",
            "Manage social media presence.",
            "Analyze market trends.",
            "Collaborate with sales teams.",
        ],
        experience: "2+ years in Marketing",
        salary: "₹16,000 – ₹26,000+",
        compensation:
            "Fixed salary with campaign-performance bonuses.",
        qualification:
            "Degree in Marketing / Communication",
        locations: ["Kolkata HQ (Hybrid)"],
    },
];

// --- FAQs ---

export const FAQS = [
    {
        question: "What is the application process?",
        answer:
            "Candidates can apply online by submitting their basic details and profile. Shortlisted candidates will be contacted for an interview or skill assessment.",
    },
    {
        question: "What are the work hours?",
        answer:
            "Work hours are fixed and depend on the assigned role and location. Complete details will be explained during the interview.",
    },
    {
        question: "Is training provided?",
        answer:
            "Yes, training is provided based on the job role and skill level. Both freshers and experienced candidates will receive proper guidance.",
    },
    {
        question: "Is accommodation provided?",
        answer:
            "Yes, accommodation facilities are available for selected candidates, as per company policy.",
    },
    {
        question: "Is food provided?",
        answer:
            "Yes, one-time meal per day is provided.",
    },
    {
        question: "Is this a paid position?",
        answer:
            "Yes, compensation will be discussed during the interview based on skills, experience, and job role.",
    },
];
